/**
 * Shrinks the photo the camera just took, and strips what it carried inside.
 *
 * ## Why shrink
 *
 * A modern phone writes four to eight megabytes per shot. Three hundred of
 * those is two gigabytes to hold in the phone and then move off it. At 1600
 * pixels on the long side a milestone's plate is still perfectly readable —
 * that was the whole point of the photo — and the file lands around 300 KB.
 *
 * ## Why re-encode even a small photo
 *
 * Because passing it through a canvas throws away the metadata block, and that
 * block says where the person who took it was standing and at what time. The
 * position we want is recorded separately and deliberately; the one hidden
 * inside the file is a trail of somebody's day and has no business travelling
 * with a picture of a stone.
 */

/** Longest side, in pixels, after shrinking. */
const MAX_SIDE = 1600;
/** JPEG quality. Above 0.85 the file grows fast and the plate reads the same. */
const QUALITY = 0.82;

/** Decodes the file, honouring the rotation flag the camera wrote. */
async function decode(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Falls through: some phones refuse HEIC here even though they will
      // happily paint it into an <img>.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = (): void => resolve(image);
      image.onerror = (): void => reject(new Error("No se pudo abrir la foto"));
      image.src = url;
    });
  } finally {
    // Revoked after the image has loaded: the bitmap is already in memory.
    URL.revokeObjectURL(url);
  }
}

function sizeOf(source: ImageBitmap | HTMLImageElement): { width: number; height: number } {
  return source instanceof HTMLImageElement
    ? { width: source.naturalWidth, height: source.naturalHeight }
    : { width: source.width, height: source.height };
}

/** Reduces the photo and returns a plain JPEG with nothing else inside. */
export async function shrinkPhoto(file: Blob): Promise<Blob> {
  const source = await decode(file);
  const { width, height } = sizeOf(source);
  if (width === 0 || height === 0) throw new Error("La foto vino vacía");

  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext("2d");
  if (context === null) throw new Error("Este navegador no sabe redimensionar fotos");
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  if (!(source instanceof HTMLImageElement)) source.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", QUALITY);
  });
  if (blob === null) throw new Error("No se pudo guardar la foto reducida");
  return blob;
}
